import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useLocation, useNavigate } from 'react-router-dom';
import { getFirestore, collection, addDoc, doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { getStorage, ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getAuth } from 'firebase/auth';
import { formatPrice } from '../utils/priceFormatter';
import { db, storage, functions } from '../firebase';

const ListingPreviewScreen = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { artworkData, artistName, artistBirthYear, currentUserId, isEditMode, existingArtworkId, originalImageUrl } = location.state;
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadedArtwork, setUploadedArtwork] = useState(null);

  const handleConfirmListing = async () => {
    setIsUploading(true);
    setUploadProgress(0);

    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      console.error('No authenticated user found');
      setIsUploading(false);
      return;
    }

    try {
      // Step 1: Create Firestore document without image
      const categoryRefs = artworkData.categoryIds.map(id => doc(db, 'art_categories', id));
      const artistRef = doc(db, 'users', currentUserId);
      const ownerRef = doc(db, 'users', currentUserId);

      const artworkToUpload = {
        ...artworkData,
        categories: categoryRefs,
        artist: artistRef,
        owner: ownerRef,
        updatedAt: serverTimestamp(),
        isCurated: false,
        // Will be automatically updated next day when the randomizer cloud function runs.
        randomOrder: 100,
      };

      // Remove unnecessary fields
      delete artworkToUpload.categoryIds;
      delete artworkToUpload.categoryNames;
      delete artworkToUpload.image;

      let docRef;
      if (isEditMode) {
        docRef = doc(db, 'artworks', existingArtworkId);
        await updateDoc(docRef, artworkToUpload);
      } else {
        artworkToUpload.createdAt = serverTimestamp();
        docRef = await addDoc(collection(db, 'artworks'), artworkToUpload);
      }

      const artworkId = isEditMode ? existingArtworkId : docRef.id;

      // Step 2: Upload image to Storage
      let finalImageUrl;
      
      // Check if the image is a base64 string (new upload) or a URL (existing image)
      const isBase64Image = artworkData.image?.startsWith('data:');
      const hasImageChanged = isBase64Image || (artworkData.image && artworkData.image !== originalImageUrl);
      
      if (hasImageChanged) {
        const imagePath = `artworks/${artworkId}_original.jpg`;
        const imageRef = ref(storage, imagePath);

        try {
          let file;
          if (isBase64Image) {
            // Convert base64 to blob without fetching
            const base64Data = artworkData.image.split(',')[1];
            const byteCharacters = atob(base64Data);
            const byteArrays = [];
            
            for (let offset = 0; offset < byteCharacters.length; offset += 1024) {
              const slice = byteCharacters.slice(offset, offset + 1024);
              const byteNumbers = new Array(slice.length);
              
              for (let i = 0; i < slice.length; i++) {
                byteNumbers[i] = slice.charCodeAt(i);
              }
              
              const byteArray = new Uint8Array(byteNumbers);
              byteArrays.push(byteArray);
            }
            
            file = new Blob(byteArrays, { type: 'image/jpeg' });
          } else {
            // For existing image URL, we'll use the original URL
            finalImageUrl = originalImageUrl;
            // Update Firestore document with existing image URL
            await updateDoc(docRef, {
              imageOriginal: originalImageUrl,
              imageMedium: originalImageUrl,
              imageThumbnail: originalImageUrl
            });
            return finishUpload(artworkId, originalImageUrl);
          }

          const uploadTask = uploadBytesResumable(imageRef, file);

          await new Promise((resolve, reject) => {
            uploadTask.on(
              'state_changed',
              (snapshot) => {
                const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                setUploadProgress(progress);
              },
              (error) => {
                console.error('Error uploading image:', error);
                reject(error);
              },
              async () => {
                try {
                  const imageUrl = await getDownloadURL(uploadTask.snapshot.ref);
                  finalImageUrl = imageUrl;

                  // Update Firestore document with image URL
                  await updateDoc(docRef, {
                    imageOriginal: imageUrl,
                    imageMedium: imageUrl,
                    imageThumbnail: imageUrl
                  });

                  resolve();
                } catch (error) {
                  reject(error);
                }
              }
            );
          });
        } catch (error) {
          console.error('Error processing image:', error);
          throw error;
        }
      } else {
        finalImageUrl = originalImageUrl;
      }

      // Step 3: Call cloud function for further processing
      try {
        const newArtworkUpload = httpsCallable(functions, 'newArtworkUpload');
        await newArtworkUpload({
          id: artworkId,
          title: artworkToUpload.title,
          artistId: currentUserId,
          artistName: artistName,
          price: artworkToUpload.price,
          createdYear: artworkToUpload.createdYear,
          categoryNames: artworkData.categoryNames,
          isPublicArt: artworkToUpload.isPublicArt,
          isForSale: artworkToUpload.isForSale,
          materials: artworkToUpload.materials,
          size: artworkToUpload.size,
          description: artworkToUpload.description,
          allowBuyerPickUp: artworkToUpload.allowBuyerPickUp,
          zipCode: artworkToUpload.zipCode,
          imageOriginal: finalImageUrl,
          status: artworkToUpload.status,
        });
      } catch (error) {
        console.error('Error in cloud function:', error);
      }

      finishUpload(artworkId, finalImageUrl);
    } catch (error) {
      console.error('Error during the upload process:', error);
      setIsUploading(false);
      setUploadProgress(0);
      alert('Upload Failed: ' + error.message);
    }
  };

  const finishUpload = (artworkId, imageUrl) => {
    const uploadedArtworkData = {
      id: artworkId,
      ...artworkData,
      imageOriginal: imageUrl,
      artistData: {
        displayName: artistName,
        birthYear: artistBirthYear,
      },
    };

    setIsUploading(false);
    setUploadProgress(0);
    setUploadedArtwork(uploadedArtworkData);
  };

  useEffect(() => {
    if (uploadedArtwork) {
      navigate(`/artwork/${uploadedArtwork.id}`);
    }
  }, [uploadedArtwork, navigate]);

  return (
    <Container>
      <ScrollView>
        <Header>Listing Preview</Header>
        {artworkData.image && <Image src={artworkData.image} alt="Artwork preview" />}
        <Title>{artworkData.title || 'Untitled'}</Title>
        <ArtistInfo>{artistName || 'Unknown Artist'}, b. {artistBirthYear || 'Unknown'}</ArtistInfo>
        <Text>{artworkData.createdYear || 'Year not specified'}</Text>
        <CategoryContainer>
          {artworkData.categoryNames && artworkData.categoryNames.map((catName, index) => (
            <CategoryTag key={index}>
              <CategoryText>{catName.toUpperCase()}</CategoryText>
            </CategoryTag>
          ))}
        </CategoryContainer>
        <Text>{artworkData.isPublicArt ? 'Public Art' : 'Not Public Art'}</Text>
        <Text>Status: {artworkData.status}</Text>
        {artworkData.isForSale && <Text>Price: {formatPrice(artworkData.price) || 'Not specified'}</Text>}
        <Text>Materials: {artworkData.materials || 'No materials provided'}</Text>
        <Text>Size: {artworkData.size || 'No size provided'}</Text>
        <Text>Description: {artworkData.description || 'No description provided'}</Text>
        {artworkData.allowBuyerPickUp && <Text>Pickup allowed. Zip Code: {artworkData.zipCode}</Text>}

        {isUploading ? (
          <UploadingContainer>
            <ProgressBar $progress={uploadProgress} />
            <UploadingText>Uploading: {uploadProgress.toFixed(0)}%</UploadingText>
          </UploadingContainer>
        ) : (
          <ConfirmButton onClick={handleConfirmListing}>
            Confirm Listing
          </ConfirmButton>
        )}

        <EditButton onClick={() => navigate(-1)}>
          Edit Listing
        </EditButton>
      </ScrollView>
    </Container>
  );
};

const Container = styled.div`
  flex: 1;
  background-color: white;
  min-height: 100vh;
`;

const ScrollView = styled.div`
  padding: 20px;
  padding-bottom: 40px;
  max-width: 800px;
  margin: 0 auto;
`;

const Header = styled.h1`
  font-size: 24px;
  font-weight: bold;
  margin-bottom: 20px;
  text-align: center;
`;

const Image = styled.img`
  width: 100%;
  max-height: 300px;
  object-fit: contain;
  margin-bottom: 20px;
`;

const Title = styled.h2`
  font-size: 20px;
  font-weight: bold;
  margin-bottom: 10px;
`;

const ArtistInfo = styled.p`
  font-size: 16px;
  font-style: italic;
  margin-bottom: 10px;
`;

const Text = styled.p`
  margin-bottom: 10px;
`;

const CategoryContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-top: 10px;
`;

const CategoryTag = styled.div`
  background-color: black;
  padding: 5px 10px;
  border-radius: 15px;
  margin-right: 10px;
  margin-bottom: 10px;
`;

const CategoryText = styled.span`
  color: white;
  font-weight: bold;
`;

const UploadingContainer = styled.div`
  margin: 20px 0;
  text-align: center;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 4px;
  background-color: #eee;
  border-radius: 2px;
  overflow: hidden;
  position: relative;

  &:after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: ${props => props.$progress}%;
    background-color: #000;
    transition: width 0.3s ease;
  }
`;

const UploadingText = styled.p`
  margin-top: 10px;
  font-size: 14px;
  color: #666;
`;

const Button = styled.button`
  width: 100%;
  padding: 15px;
  border-radius: 5px;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
  border: none;
  margin-top: 10px;

  &:hover {
    opacity: 0.9;
  }
`;

const ConfirmButton = styled(Button)`
  background-color: #FF1493;
  color: white;
`;

const EditButton = styled(Button)`
  background-color: #eee;
  color: black;
`;

export default ListingPreviewScreen; 